"use client";

import { Layers3, Sparkles } from "lucide-react";
import { makeFunctionReference } from "convex/server";
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  suggestBulkActions,
  summarizeBulkAction,
  type BulkActionLoad,
  type BulkActionSuggestion,
} from "@/core/console/bulk-actions";

const stageBulkAction = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    userId: string;
    loadIds: string[];
    actionType: BulkActionSuggestion["actionType"];
    suggestion: Record<string, unknown>;
    actor: "dispatcher";
    actorId: string;
  },
  { batchId: string; staged: Array<{ loadId: string; actionId?: string; approvalId?: string; status: string }> }
>("bulkActions:stageBulkAction");

export function BulkActionsPanel({
  tenantId,
  userId,
  loads,
}: {
  tenantId: string;
  /** Server-derived authenticated user id: undefined = resolving, null = signed out. */
  userId?: string | null;
  loads: BulkActionLoad[];
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) return <BulkActionsContent loads={loads} />;
  if (!userId) {
    // Fail closed: never stage actions under a fabricated identity.
    return (
      <BulkActionsContent
        loads={loads}
        stagingDisabledReason={
          userId === null
            ? "Sign in with an active tenant membership to stage bulk actions."
            : "Resolving your identity…"
        }
      />
    );
  }
  return <BulkActionsLive tenantId={tenantId} userId={userId} loads={loads} />;
}

function BulkActionsLive(props: { tenantId: string; userId: string; loads: BulkActionLoad[] }) {
  const stage = useMutation(stageBulkAction);
  return (
    <BulkActionsContent
      loads={props.loads}
      onStage={(suggestion) =>
        stage({
          tenantId: props.tenantId,
          userId: props.userId,
          loadIds: suggestion.loadIds,
          actionType: suggestion.actionType,
          suggestion: { title: suggestion.title, reason: suggestion.reason },
          actor: "dispatcher",
          actorId: props.userId,
        })
      }
    />
  );
}

function BulkActionsContent({
  loads,
  onStage,
  stagingDisabledReason,
}: {
  loads: BulkActionLoad[];
  onStage?: (suggestion: BulkActionSuggestion) => Promise<unknown>;
  stagingDisabledReason?: string;
}) {
  const suggestions = useMemo(() => suggestBulkActions(loads), [loads]);
  const [lastResult, setLastResult] = useState<string | null>(null);

  async function stageSuggestion(suggestion: BulkActionSuggestion) {
    await onStage?.(suggestion);
    setLastResult(`${summarizeBulkAction(suggestion, loads)} staged${suggestion.requiresApproval ? " for approval" : ""}`);
  }

  return (
    <section id="bulk-actions" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Bulk Assist</h2>
          <p className="text-sm text-muted-foreground">Agent-suggested bulk actions, staged per load.</p>
        </div>
        <Badge variant="info">{suggestions.length} suggestions</Badge>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles size={16} />
            Suggested Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 lg:grid-cols-2">
          {suggestions.map((suggestion) => (
            <div key={suggestion.actionType} className="rounded-md border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 font-medium">
                    <Layers3 size={16} />
                    {suggestion.title}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">{suggestion.reason}</div>
                </div>
                <Badge variant={suggestion.requiresApproval ? "warn" : "good"}>
                  {suggestion.requiresApproval ? "approval" : "stage"}
                </Badge>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">{suggestion.loadIds.length} loads</span>
                <Button
                  variant="secondary"
                  disabled={Boolean(stagingDisabledReason)}
                  onClick={() => stageSuggestion(suggestion)}
                >
                  Stage
                </Button>
              </div>
            </div>
          ))}
          {suggestions.length === 0 ? (
            <div className="rounded-md border border-border p-4 text-sm text-muted-foreground">No bulk action suggested.</div>
          ) : null}
        </CardContent>
      </Card>
      {stagingDisabledReason ? (
        <div className="text-sm text-amber-700">{stagingDisabledReason}</div>
      ) : null}
      {lastResult ? <div className="text-sm text-muted-foreground">{lastResult}</div> : null}
    </section>
  );
}
